import typography from '@amblique/king-living/app/theme/foundations/typography';

const CheckoutConfirmationPage = {
  parts: [
    'pageContainer',
    'contentWrapper',
    'contentGrid',
    'headingColumn',
    'pageHeading',
    'pageSubcopy',
    'contentColumn',
    'contentStack',
    'summaryColumn',
    'sidebarStack',
    'section',
    'sectionHeading',
    'summaryCard',
    'summaryMetaStack',
    'summaryMetaDate',
    'summaryMetaOrderNo',
    'lineItemList',
    'lineItemRow',
    'lineItemThumbnail',
    'lineItemContent',
    'lineItemDetailsGroup',
    'lineItemTitle',
    'lineItemDetail',
    'lineItemPromo',
    'lineItemQty',
    'lineItemResourceLinks',
    'lineItemResourceLink',
    'lineItemPriceColumn',
    'lineItemPrice',
    'lineItemStrikePrice',
    'totalsStack',
    'totalsRows',
    'totalsRow',
    'totalsSubtotalRow',
    'totalsDivider',
    'paymentSummaryStack',
    'totalPriceRow',
    'totalPriceLabel',
    'totalPriceValue',
    'totalPaidBlock',
    'totalPaidRow',
    'totalPaidLabel',
    'totalPaidValue',
    'totalPaidMethod',
    'fieldStack',
    'fieldStackContactBilling',
    'fieldRow',
    'fieldLabel',
    'fieldValue',
    'nextSteps',
    'nextStepsHeading',
    'nextStepsList',
    'nextStepsItem',
    'nextStepsIconWrap',
    'nextStepsIcon',
    'nextStepsText',
    'createAccount',
    'createAccountContent',
    'createAccountHeading',
    'createAccountList',
    'createAccountListItem',
    'createAccountButton',
    'createAccountEmailHint',
    'createAccountEmailHintValue',
    'npsFeedbackTitle',
  ],
  baseStyle: {
    pageContainer: {
      bg: 'brandColors.primaryBG',
      flex: '1',
      maxW: '100%',
      overflowX: 'clip',
      padding: {base: '0 20px', lg: '0 80px'},
      position: 'relative',
    },
    contentWrapper: {
      marginX: 'auto',
      maxWidth: '1280px',
      minW: 0,
      paddingX: 0,
      width: '100%',
    },
    contentGrid: {
      alignItems: 'stretch',
      gap: 0,
      minW: 0,
      templateAreas: {
        base: '"heading" "content" "summary"',
        lg: '"heading summary" "content summary"',
      },
      templateColumns: {base: 'minmax(0, 1fr)', lg: '57.0318% 1fr'},
      templateRows: {base: 'auto auto auto', lg: 'auto 1fr'},
      width: '100%',
    },
    headingColumn: {
      bg: 'brandColors.primaryBG',
      gridArea: 'heading',
      paddingBottom: {base: '40px', lg: 10},
      paddingRight: {base: 0, lg: 20},
      paddingTop: {base: '48px', lg: 20},
    },
    pageHeading: {
      ...typography.textStyles.headingXL,
      color: 'grayAlpha.90',
      letterSpacing: {base: '-0.32px', lg: '-0.4px'},
    },
    pageSubcopy: {
      ...typography.textStyles.bodyL,
      color: 'grayAlpha.90',
      marginTop: {base: 4, lg: 6},
    },
    contentColumn: {
      bg: 'brandColors.primaryBG',
      gridArea: 'content',
      maxW: '100%',
      minW: 0,
      paddingBottom: {base: '80px', lg: '80px'},
      paddingRight: {base: 0, lg: '80px'},
    },
    contentStack: {
      gap: {base: 8, lg: 12},
      paddingTop: {base: 0, lg: 0},
    },
    summaryColumn: {
      bg: {base: 'whiteAlpha.700', lg: 'transparent'},
      borderBottomWidth: {base: '1px', lg: 0},
      borderColor: 'blackAlpha.100',
      borderStyle: 'solid',
      borderTopWidth: {base: '0', lg: '0'},
      boxSizing: 'border-box',
      gridArea: 'summary',
      marginLeft: {base: '-20px', lg: 'unset'},
      marginRight: {base: '-20px', lg: 'unset'},
      maxWidth: {base: 'unset', lg: '550px'},
      minW: 0,
      paddingBottom: {base: '48px', lg: '80px'},
      paddingLeft: {base: '20px', lg: '80px'},
      paddingRight: {base: '20px', lg: 'unset'},
      paddingTop: {base: '48px', lg: '80px'},
      position: 'relative',
      width: {base: 'calc(100% + 40px)', lg: 'auto'},
      sx: {
        '&:before': {
          backgroundColor: {base: 'transparent', lg: 'whiteAlpha.700'},
          content: '""',
          display: {base: 'none', lg: 'block'},
          height: '100%',
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          width: '100vw',
          zIndex: 0,
        },
      },
    },
    sidebarStack: {
      gap: {base: '48px', lg: 10},
      maxW: '100%',
      minW: 0,
      position: 'relative',
      width: '100%',
      zIndex: 1,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: {base: '24px', lg: 8},
      maxW: '100%',
      minW: 0,
      width: '100%',
      sx: {
        '& .confirmation-field-row': {
          '& .confirmation-field-label': {
            color: 'greyscale.70',
          },
        },
      },
    },
    sectionHeading: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontSize: {base: '20px', lg: '32px'},
      fontWeight: 500,
      lineHeight: {base: 1.5, lg: 1.4},
      width: '100%',
    },
    summaryCard: {
      bg: 'white',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      maxW: '100%',
      minW: 0,
      padding: {base: '24px 16px', lg: '40px'},
      width: '100%',
    },
    summaryMetaStack: {
      gap: '7px',
    },
    summaryMetaDate: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: '16px',
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: {base: 500, lg: 600},
      lineHeight: {base: 1.6, lg: 1.4},
    },
    summaryMetaOrderNo: {
      color: 'greyscale.60',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '14px', lg: '16px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: {base: 400, lg: 600},
      lineHeight: 1.4,
    },
    lineItemList: {
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 3, lg: 4},
      width: '100%',
      sx: {
        '& > .confirmation-line-item:first-of-type': {
          borderTopWidth: {lg: '1px'},
        },
      },
    },
    lineItemRow: {
      alignItems: 'flex-start',
      borderBottomWidth: {lg: '1px'},
      borderColor: 'grayAlpha.10',
      borderStyle: 'solid',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: {base: 4, lg: 6},
      maxW: '100%',
      minW: 0,
      paddingBottom: {base: '0', lg: '24px'},
      paddingTop: {base: 0, lg: '24px'},
      width: '100%',
    },
    lineItemThumbnail: {
      bg: 'brandColors.secondaryBGTranslucent15',
      flexShrink: 0,
      h: {base: '72px', lg: '120px'},
      overflow: 'hidden',
      w: {base: '72px', lg: '120px'},
    },
    lineItemContent: {
      display: 'flex',
      flex: '1 1 0',
      flexDirection: 'column',
      gap: {base: 2, lg: '8px'},
      justifyContent: 'flex-start',
      minW: 0,
      py: '8px',
      width: '100%',
    },
    lineItemDetailsGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      width: '100%',
    },
    lineItemTitle: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 600,
      lineHeight: 1.4,
      minW: 0,
      width: '100%',
    },
    lineItemDetail: {
      color: 'grayAlpha.70',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
    },
    lineItemPromo: {
      color: 'secondary.newSaleRed',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
    },
    lineItemQty: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
    },
    lineItemResourceLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      width: '100%',
      marginTop: {base: '16px', lg: '16px'},
      whiteSpace: {'base': 'nowrap', 'lg': 'unset'},
    },
    lineItemResourceLink: {
      alignItems: 'center',
      color: 'rgba(50, 49, 47, 0.90)',
      display: 'inline-flex',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 600,
      gap: '8px',
      lineHeight: 1.2,
      width: 'fit-content',
      _hover: {
        color: 'rgba(50, 49, 47, 0.90)',
        textDecoration: 'none',
      },
      sx: {
        svg: {
          color: 'brandColors.primaryDarkLinen',
        },
      },
    },
    lineItemPriceColumn: {
      alignItems: 'flex-end',
      alignSelf: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      gap: '6px',
      py: '8px',
      textAlign: 'right',
    },
    lineItemPrice: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 600,
      lineHeight: 1.4,
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    lineItemStrikePrice: {
      color: 'greyscale.60',
      fontFamily: 'body',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'right',
      textDecoration: 'line-through',
      whiteSpace: 'nowrap',
    },
    totalsStack: {
      gap: {base: '14px', lg: '14px'},
      width: '100%',
    },
    totalsRows: {
      gap: '14px',
      sx: {
        '& .confirmation-order-summary__totals-row': {
          borderBottomColor: 'grayAlpha.10',
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          paddingBottom: {base:'14px', lg: '14px'},
        },
        '& .confirmation-order-summary__subtotal-row:has(+ .confirmation-order-summary__savings-row)': {
          borderBottomWidth: '0',
          paddingBottom: {base:'0', lg: '0'},
        },
        '& .confirmation-order-summary__totals-row:last-of-type': {
          borderBottomWidth: '0',
          paddingBottom: {base:'0', lg: '0'},
        },
      }
    },
    totalsRow: {
      alignItems: 'center',
      color: 'grayAlpha.70',
      display: 'flex',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 400,
      gap: 4,
      justifyContent: 'space-between',
      lineHeight: 1.4,
      maxW: '100%',
      minW: 0,
      width: '100%',
      
      sx: {
        '& > *': {
          minW: 0,
        },
      },
    },
    totalsSubtotalRow: {
      borderTopColor: 'grayAlpha.10',
      borderTopStyle: 'solid',
      borderTopWidth: {base: '1px', lg: 0},
      paddingTop: {base: '14px', lg: 0},
    },
    totalsDivider: {
      bg: 'grayAlpha.10',
      height: '1px',
      width: '100%',
      sx: {
        '&.i9-line-item': {
          border: 'none',
          my: {base: '0', lg: '0'},
        },
      },
    },
    paymentSummaryStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      maxW: '100%',
      minW: 0,
      width: '100%',
    },
    totalPriceRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: 4,
      justifyContent: 'space-between',
      maxW: '100%',
      minW: 0,
      paddingTop: '10px',
      width: '100%',
      sx: {
        '& > *': {
          minW: 0,
        },
      },
    },
    totalPriceLabel: {
      color: 'greyscale.100',
      flex: '1 0 0',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '20px', lg: '24px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: {base: 1.6, lg: 1.4},
    },
    totalPriceValue: {
      color: 'greyscale.100',
      flex: '1 0 0',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '20px', lg: '24px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: {base: 1.6, lg: 1.4},
      textAlign: 'right',
    },
    totalPaidBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      maxW: '100%',
      minW: 0,
      width: '100%',
    },
    totalPaidRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: 4,
      justifyContent: 'space-between',
      maxW: '100%',
      minW: 0,
      width: '100%',
      sx: {
        '& > *': {
          minW: 0,
        },
      },
    },
    totalPaidLabel: {
      ...typography.textStyles.labelDefault,
      color: 'greyscale.100',
      flex: '1 0 0',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
    totalPaidValue: {
      ...typography.textStyles.labelDefault,
      color: '#000',
      flex: '1 0 0',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontVariantNumeric: 'lining-nums proportional-nums',
      textAlign: 'right',
    },
    totalPaidMethod: {
      ...typography.textStyles.bodyDefault,
      color: 'grayAlpha.80',
      width: '100%',
    },
    fieldStack: {
      //gap: '4px',
      width: '100%',
    },
    fieldStackContactBilling: {
      width: '100%',
      gap: {base: '0', lg: '0'},
      sx: {
        '& .confirmation-field-row': {
          '& .confirmation-field-label': {
            color: 'greyscale.70',
          },
        },
        '&.confirmation-contact-billing-fields .confirmation-field-row:last-of-type': {
          borderBottomWidth: 0,
        },
      },
    },
    fieldRow: {
      borderTopWidth: '1px',
      borderColor: 'grayAlpha.10',
      borderStyle: 'solid',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      paddingY: '16px',
      width: '100%',
      _last: {
        borderBottomWidth: {base: '1px', lg: '0'},
      },
    },
    fieldLabel: {
      color: 'grayAlpha.60',
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: {base: '13px', lg: '16px'},
      fontWeight: {base: 400, lg: 600},
      lineHeight: {base: 1.2, lg: 1.4},
      width: '100%',
    },
    fieldValue: {
      color: 'grayAlpha.90',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 400,
      lineHeight: 1.4,
    },
    nextSteps: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      width: '100%',
    },
    nextStepsHeading: {
      color: '#000',
      fontFamily: 'body',
      fontSize: {base: '20px', lg: '32px'},
      fontWeight: 500,
      lineHeight: {base: 1.5, lg: 1.4},
      width: '100%',
    },
    nextStepsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 6, lg: '17px'},
      width: '100%',
    },
    nextStepsItem: {
      alignItems: {base: 'center', lg: 'flex-start'},
      display: 'flex',
      gap: {base: 6, lg: '15px'},
      width: '100%',
    },
    nextStepsIconWrap: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      paddingTop: {base: 0, lg: '4px'},
    },
    nextStepsIcon: {
      boxSize: {base: '32px', lg: '24px'},
      color: 'primary.darkLinen',
    },
    nextStepsText: {
      color: '#000',
      flex: '1 0 0',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 400,
      lineHeight: 1.4,
      minW: 0,
    },
    createAccount: {
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 4, lg: 4},
      width: '100%',
    },
    createAccountContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: '100%',
      sx: {
        '& > ul': {
          minH: {base: '120px', lg: '94px'},
        },
      },
    },
    createAccountHeading: {
      color: '#000',
      fontFamily: 'body',
      fontSize: '24px',
      fontWeight: 500,
      lineHeight: 1.4,
      width: '100%',
    },
    createAccountList: {
      color: '#000',
      fontFamily: 'body',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 400,
      lineHeight: 1.4,
      listStylePosition: 'outside',
      listStyleType: 'disc',
      margin: 0,
      paddingInlineStart: {base: '21px', lg: '24px'},
      width: '100%',
    },
    createAccountListItem: {
      marginBottom: '8px',
      _last: {
        marginBottom: 0,
      },
    },
    createAccountButton: {
      alignSelf: {base: 'stretch', lg: 'flex-start'},
      bg: 'greyscale.100',
      border: '1px solid',
      borderColor: 'greyscale.100',
      borderRadius: 'base',
      color: 'white',
      flexShrink: 0,
      fontFamily: 'body',
      fontFeatureSettings: "'lnum' 1, 'pnum' 1",
      fontSize: '16px',
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 600,
      height: '54px',
      lineHeight: 1.4,
      minH: '54px',
      minW: 'unset',
      px: 12,
      py: 4,
      width: {base: '100%', lg: 'auto'},
      marginTop: {base: '32px', lg: '13px'},
      _hover: {
        bg: 'brandColors.charcoal',
        borderColor: 'brandColors.charcoal',
        color: 'white',
      },
      _active: {
        bg: 'brandColors.charcoal',
        borderColor: 'brandColors.charcoal',
        color: 'white',
      },
      sx: {
        height: '54px',
        minHeight: '54px',
        minWidth: 'unset',
        maxWidth: {base: '100%', lg: '234px'},
      },
    },
    createAccountEmailHint: {
      color: 'grayAlpha.50',
      fontFamily: 'body',
      fontSize: {base: '12px', lg: '14px'},
      fontWeight: 400,
      lineHeight: 1.4,
    },
    createAccountEmailHintValue: {
      color: '#000',
      fontWeight: 400,
    },
    npsFeedbackTitle: {
      color: 'greyscale.100',
      fontFamily: 'body',
      fontFeatureSettings: {base: "'lnum' 1, 'pnum' 1", lg: 'normal'},
      fontSize: {base: '16px', lg: '32px'},
      fontWeight: 500,
      lineHeight: {base: 1.6, lg: 1.4},
      mb: 4,
      textAlign: {base: 'left', lg: 'center'},
      width: '100%',
    },
  },
};

export default CheckoutConfirmationPage;
